/**
 * Choose Password
 *
 * One page serving both emailed link flows, because to the person reading the
 * email they are the same act: prove you hold the link, pick a password.
 *   /set-password?token=...    an admin invite, activates the account
 *   /reset-password?token=...  a password reset
 *
 * The token in the URL is the only credential, so the page never asks for the
 * current password and never reveals whose account it belongs to. A bad or
 * spent link gets one honest message and a way forward, not a dead end.
 */

import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Alert, IconButton,
  InputAdornment, LinearProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, East } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useThemeMode, ForceLightTheme } from '../../context/ThemeContext';
import { setPasswordFromInvite, confirmPasswordReset } from '../../services/auth';

const MIN_LENGTH = 8;

// Matches the server rule exactly, so the page never accepts what the API
// will reject.
const strength = (pw) => {
  if (!pw) return { score: 0, label: '' };
  let score = 0;
  if (pw.length >= MIN_LENGTH) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[0-9]/.test(pw) && /[a-zA-Z]/.test(pw)) score += 1;
  if (/[^a-zA-Z0-9]/.test(pw)) score += 1;
  return { score, label: ['Too short', 'Weak', 'Fair', 'Good', 'Strong'][score] };
};

function ChoosePasswordInner({ mode }) {
  const { palette } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isInvite = mode === 'invite';
  const copy = isInvite
    ? {
        title: 'Choose your password',
        lede: 'Set a password to finish activating your Farmgate account.',
        cta: 'Activate my account',
        okTitle: 'Your account is ready',
      }
    : {
        title: 'Choose a new password',
        lede: 'Pick a new password for your Farmgate account.',
        cta: 'Update my password',
        okTitle: 'Password updated',
      };

  const pwStrength = strength(password);
  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    token && password.length >= MIN_LENGTH && confirm === password && !submitting;

  const fieldSx = {
    sx: {
      color: palette.text,
      bgcolor: palette.card,
      '& fieldset': { borderColor: palette.cardBorder },
      '&:hover fieldset': { borderColor: palette.accent },
      '&.Mui-focused fieldset': { borderColor: palette.accent },
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    try {
      if (isInvite) {
        await setPasswordFromInvite(token, password);
      } else {
        await confirmPasswordReset(token, password);
      }
      setDone(true);
    } catch (err) {
      setError(err?.message || 'That did not work. Please request a new link.');
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children) => (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: palette.bg }}>
      <Container maxWidth="xs">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Typography
            component={RouterLink}
            to="/"
            sx={{
              display: 'block', mb: 3, fontWeight: 700, fontSize: '1.05rem',
              color: palette.accent, textDecoration: 'none', letterSpacing: '0.01em',
            }}
          >
            Farmgate
          </Typography>
          {children}
        </motion.div>
      </Container>
    </Box>
  );

  // No token at all: someone opened the bare URL.
  if (!token) {
    return shell(
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: palette.text }}>
          This link is incomplete
        </Typography>
        <Typography sx={{ color: palette.textSecondary, fontSize: '0.9rem', mb: 3 }}>
          Open the link straight from your email, or request a new one from the sign-in page.
        </Typography>
        <Button component={RouterLink} to="/login" variant="contained" disableElevation
          sx={{ bgcolor: palette.accent, textTransform: 'none', fontWeight: 600, borderRadius: '10px', '&:hover': { bgcolor: '#1C9550' } }}>
          Go to sign in
        </Button>
      </Box>
    );
  }

  if (done) {
    return shell(
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: palette.text }}>
          {copy.okTitle}
        </Typography>
        <Typography sx={{ color: palette.textSecondary, fontSize: '0.9rem', mb: 3 }}>
          You can sign in with your new password now.
        </Typography>
        <Button
          onClick={() => navigate('/login', { replace: true })}
          variant="contained"
          disableElevation
          endIcon={<East sx={{ fontSize: '16px !important' }} />}
          sx={{ bgcolor: palette.accent, textTransform: 'none', fontWeight: 600, borderRadius: '10px', '&:hover': { bgcolor: '#1C9550' } }}
        >
          Sign in
        </Button>
      </Box>
    );
  }

  return shell(
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: palette.text }}>
        {copy.title}
      </Typography>
      <Typography sx={{ color: palette.textSecondary, fontSize: '0.9rem', mb: 3 }}>
        {copy.lede}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
          <Box sx={{ mt: 1 }}>
            <RouterLink to="/forgot-password" style={{ color: palette.accent, fontWeight: 600, fontSize: '0.85rem' }}>
              Request a new link
            </RouterLink>
          </Box>
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="New password"
          type={showPw ? 'text' : 'password'}
          required fullWidth size="small" autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          error={tooShort}
          helperText={tooShort ? `At least ${MIN_LENGTH} characters` : ' '}
          InputLabelProps={{ sx: { color: palette.textMuted } }}
          InputProps={{
            ...fieldSx,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPw((v) => !v)}
                  edge="end" size="small"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  sx={{ color: palette.textMuted }}
                >
                  {showPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {password.length > 0 && (
          <Box sx={{ mt: -1.5 }}>
            <LinearProgress
              variant="determinate"
              value={(pwStrength.score / 4) * 100}
              sx={{
                height: 4, borderRadius: 2, bgcolor: palette.surfaceHover,
                '& .MuiLinearProgress-bar': {
                  bgcolor: pwStrength.score <= 1 ? palette.danger : pwStrength.score === 2 ? palette.warning : palette.accent,
                },
              }}
            />
            <Typography sx={{ fontSize: '0.72rem', color: palette.textMuted, mt: 0.5 }}>
              {pwStrength.label}
            </Typography>
          </Box>
        )}

        <TextField
          label="Confirm password"
          type={showPw ? 'text' : 'password'}
          required fullWidth size="small"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          error={mismatch}
          helperText={mismatch ? 'Both entries must match' : ' '}
          InputLabelProps={{ sx: { color: palette.textMuted } }}
          InputProps={fieldSx}
        />

        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={!canSubmit}
          endIcon={<East sx={{ fontSize: '16px !important' }} />}
          sx={{
            bgcolor: palette.accent, color: '#fff', fontWeight: 600, py: 1.2,
            borderRadius: '10px', textTransform: 'none',
            '&:hover': { bgcolor: '#1C9550' },
            '&.Mui-disabled': { bgcolor: palette.cardBorder, color: palette.textMuted },
          }}
        >
          {submitting ? 'Saving…' : copy.cta}
        </Button>
      </Box>
    </Box>
  );
}

function ChoosePassword({ mode }) {
  const params = useParams();
  return (
    <ForceLightTheme>
      <ChoosePasswordInner mode={mode || params.mode} />
    </ForceLightTheme>
  );
}

export default ChoosePassword;
